import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { X, MessageSquare, Send, User, MapPin, Users, Calendar, Clock, Mail, FileText, Check, ChevronDown, Building2 } from 'lucide-react';
import tableApi from '../../services/tableApi';
import toast from 'react-hot-toast';
import type { RestaurantTable } from '../../types/reservations';
import { useMergeGroups } from '../../context/MergeGroupContext';

interface SMSDialogProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSend: (message: string, selectedTableIds: number[], mergeGroupName?: string, mergeGroupColor?: string) => void;
  readonly reservation: {
    id: string;
    customerName: string;
    email: string;
    phone: string;
    date: string;
    time: string;
    guestNo: number;
    assignedTable?: string;
    specialRequests?: string;
  };
  readonly action: 'accept' | 'reject';
}

export default function SMSDialog({ isOpen, onClose, onSend, reservation, action }: SMSDialogProps) {
  const { mergeGroups } = useMergeGroups();
  const [message, setMessage] = useState('');
  const [selectedTableIds, setSelectedTableIds] = useState<number[]>([]);
  const [tables, setTables] = useState<RestaurantTable[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showTableDropdown, setShowTableDropdown] = useState(false);
  const [selectedFloorId, setSelectedFloorId] = useState<number>(1); // Default to floor 1
  
  // Merge group creation state (for large parties)
  const [mergeGroupName, setMergeGroupName] = useState('');
  const [mergeGroupColor, setMergeGroupColor] = useState('#3B82F6');
  
  const isLargeParty = reservation.guestNo >= 4;

  // Load tables effect - separate from initialization
  const [isLoadingTables, setIsLoadingTables] = useState(false);
  
  useEffect(() => {
    if (isOpen && action === 'accept') {
      const loadTablesData = async () => {
        setIsLoadingTables(true);
        try {
          
          // Use the new endpoint for accurate availability based on reservation date/time
          const availableTables = await tableApi.getAvailableTablesByDateTime(
            reservation.date,
            reservation.time,
            selectedFloorId
          );
          
          setTables(availableTables);
          
        } catch (error) {
          console.error('Failed to load tables:', error);
          toast.error('Failed to load tables. Please try again.');
          setTables([]);
        } finally {
          setIsLoadingTables(false);
        }
      };
      
      loadTablesData();
    }
  }, [isOpen, action, selectedFloorId, reservation.date, reservation.time]);


  // Initialize dialog state when opened
  useEffect(() => {
    if (isOpen) {
      // Set default message based on action
      const tableInfo = reservation.assignedTable ? ` You have been assigned to Table ${reservation.assignedTable}.` : '';
      const defaultMessage = action === 'accept'
        ? `Hi ${reservation.customerName}, your reservation for ${reservation.guestNo} people on ${reservation.date} at ${reservation.time} has been accepted!${tableInfo} We look forward to welcoming you to Grand Pittu.`
        : `Hi ${reservation.customerName}, unfortunately we need to cancel your reservation for ${reservation.date} at ${reservation.time}. We apologize for any inconvenience. Please call us to reschedule.`;
      
      setMessage(defaultMessage);
      setSelectedTableIds([]);
      setMergeGroupName('');
      setMergeGroupColor('#3B82F6');
      
      // Suggest merge group name for large parties
      if (isLargeParty) {
        setMergeGroupName(`${reservation.customerName} Party`);
      }
    }
  }, [isOpen, action, reservation.customerName, reservation.guestNo, reservation.date, reservation.time, reservation.assignedTable, isLargeParty]);

  // Auto-update message when table selection changes
  useEffect(() => {
    if (isOpen && action === 'accept' && selectedTableIds.length > 0) {
      const selectedTables = tables.filter(t => selectedTableIds.includes(t.id));
      const tableNames = selectedTables.map(t => t.name).join(', ');
      const tableInfo = ` You have been assigned to ${tableNames}.`;
      
      const updatedMessage = `Hi ${reservation.customerName}, your reservation for ${reservation.guestNo} people on ${reservation.date} at ${reservation.time} has been accepted!${tableInfo} We look forward to welcoming you to Grand Pittu.`;
      setMessage(updatedMessage);
    }
  }, [selectedTableIds, tables, isOpen, action, reservation]);

  const handleSend = () => {
    // Validation
    if (action === 'accept' && selectedTableIds.length === 0) {
      toast.error('Please select at least one table');
      return;
    }
    
    // For large parties with multiple tables, require merge group name
    if (selectedTableIds.length >= 2 && !mergeGroupName.trim()) {
      toast.error('Please enter a name for the merged table group');
      return;
    }
    
    setIsLoading(true);
    onSend(
      message, 
      action === 'accept' ? selectedTableIds : [], 
      selectedTableIds.length >= 2 ? mergeGroupName : undefined,
      selectedTableIds.length >= 2 ? mergeGroupColor : undefined
    );
    setIsLoading(false);
    onClose();
  };
  
  const handleTableToggle = (tableId: number) => {
    // Check if table is part of a merge group
    const tableInMergeGroup = mergeGroups.find((group) =>
      group.restaurantTableId?.some((t) => t.id === tableId)
    );

    if (tableInMergeGroup) {
      // Cannot select tables that are already in merge groups
      toast.error(
        `Table is already part of "${tableInMergeGroup.name}". Please unmerge first.`,
        {
          duration: 3000,
        }
      );
      return;
    }

    setSelectedTableIds(prev => {
      if (prev.includes(tableId)) {
        return prev.filter(id => id !== tableId);
      } else {
        return [...prev, tableId];
      }
    });
  };
  
  const getSelectedTablesText = () => {
    if (selectedTableIds.length === 0) return 'Select table(s)...';
    const selectedNames = tables.filter(t => selectedTableIds.includes(t.id)).map(t => t.name);
    return selectedNames.join(', ');
  };
  
  const getTotalCapacity = () => {
    return tables.filter(t => selectedTableIds.includes(t.id)).reduce((sum, t) => sum + t.seats, 0);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-2xl max-h-[80vh] overflow-y-auto bg-white dark:bg-gray-900">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            <CardTitle className='text-blue-500'>
              {action === 'accept' ? 'Accept Reservation & Send Message' : 'Reject Reservation & Send Message'}
            </CardTitle>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
          >
            <X className="h-4 w-4 text-red-500" />
          </Button>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Reservation Details */}
          <div className="p-3 bg-gray-50 rounded-lg">
            <CardDescription className="text-sm font-medium mb-2">Reservation Details</CardDescription>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4" />
                <span>{reservation.customerName}</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                <span className="text-xs">{reservation.email}</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                <span>{reservation.guestNo} guests</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>{reservation.date}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span>{reservation.time}</span>
              </div>
              {reservation.assignedTable && (
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  <span>Table {reservation.assignedTable}</span>
                </div>
              )}
            </div>
            {reservation.specialRequests && (
              <div className="mt-2 pt-2 border-t border-gray-200">
                <div className="flex items-start gap-2 text-xs">
                  <FileText className="h-3 w-3 mt-0.5" />
                  <div>
                    <span className="font-medium">Special Requests:</span>
                    <p className="text-gray-600 mt-1">{reservation.specialRequests}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Table Selection (only for accept) - Multi-select for large parties */}
          {action === 'accept' && (
            <div className="space-y-3">
              {isLargeParty && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-2 text-blue-800 text-sm">
                    <Users className="h-4 w-4" />
                    <span className="font-medium">Large Party ({reservation.guestNo} guests) - Select multiple tables to merge</span>
                  </div>
                </div>
              )}
              
              {/* Floor Selection */}
              <div className="space-y-2">
                <Label htmlFor="floor-select" className="text-sm font-medium flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Select Floor
                </Label>
                <select
                  id="floor-select"
                  value={selectedFloorId}
                  onChange={(e) => setSelectedFloorId(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={1}>1st Floor</option>
                  <option value={2}>2nd Floor</option>
                  {/* <option value={3}>Roof Top</option> */}
                </select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="table-select" className="text-sm font-medium flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  {isLargeParty ? 'Select Tables to Merge' : 'Assign Table'}
                </Label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowTableDropdown(!showTableDropdown)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-left bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center justify-between"
                  >
                    <span className={selectedTableIds.length === 0 ? "text-gray-400" : "text-gray-900"}>
                      {getSelectedTablesText()}
                    </span>
                    <ChevronDown className="w-4 h-4 text-gray-500" />
                  </button>

                  {showTableDropdown && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {(() => {
                        if (isLoadingTables) {
                          return (
                            <div className="px-3 py-4 text-sm text-gray-500 flex items-center justify-center gap-2">
                              <svg className="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Loading available tables...
                            </div>
                          );
                        }
                        
                        if (tables.length === 0) {
                          return (
                            <div className="px-3 py-2 text-sm text-gray-500">
                              No available tables found
                            </div>
                          );
                        }
                        
                        return (
                        <div className="p-2">
                          {/* Group tables by floor */}
                          {Array.from(new Set(tables.map(t => t.floor))).sort((a, b) => Number(a) - Number(b)).map((floor) => {
                            const floorTables = tables.filter(t => t.floor === floor);
                            const availableTables = floorTables.filter(
                              (table) =>
                                !mergeGroups.some((g) =>
                                  g.restaurantTableId?.some(
                                    (rt) => rt.id === table.id
                                  )
                                )
                            );
                            const mergedTables = floorTables.filter((table) =>
                              mergeGroups.some((g) =>
                                g.restaurantTableId?.some(
                                  (rt) => rt.id === table.id
                                )
                              )
                            );

                            return (
                              <div key={floor}>
                                {/* Floor Header */}
                                <div className="px-2 py-1 text-xs font-semibold text-gray-500 bg-gray-100 rounded mt-1 mb-1">
                                  Floor {floor}
                                </div>

                                {/* Available Tables */}
                                {availableTables.map((table) => (
                                  <label
                                    key={table.id}
                                    className="flex items-center gap-2 px-2 py-2 hover:bg-gray-50 rounded cursor-pointer"
                                  >
                                    <Checkbox
                                      checked={selectedTableIds.includes(table.id)}
                                      onCheckedChange={() =>
                                        handleTableToggle(table.id)
                                      }
                                    />
                                    <span className="text-sm text-gray-900 flex-1">
                                      {table.name}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                      {table.seats} seats · {table.status}
                                    </span>
                                  </label>
                                ))}

                                {/* Tables in merge groups (disabled) */}
                                {mergedTables.map((table) => {
                                  const group = mergeGroups.find((g) =>
                                    g.restaurantTableId?.some(
                                      (rt) => rt.id === table.id
                                    )
                                  );
                                  return (
                                    <label
                                      key={table.id}
                                      className="flex items-center gap-2 px-2 py-2 bg-gray-50 rounded cursor-not-allowed opacity-60"
                                      title={`Part of merge group: ${group?.name}`}
                                    >
                                      <Checkbox checked={false} disabled={true} />
                                      <span className="text-sm text-gray-500 flex-1">
                                        {table.name}
                                      </span>
                                      <span className="text-xs text-gray-400">
                                        In group: {group?.name}
                                      </span>
                                    </label>
                                  );
                                })}
                              </div>
                            );
                          })}
                        </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500">
                  {selectedTableIds.length} table(s) selected
                  {selectedTableIds.length > 0 && ` · Total seats: ${getTotalCapacity()} seats`}
                </p>
              </div>

              {/* Merge Group Creation UI - Shows when 2+ tables selected */}
              {selectedTableIds.length >= 2 && (
                <div className="space-y-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-blue-600" />
                    <h4 className="font-medium text-blue-900">Merge Table Group</h4>
                  </div>

                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label htmlFor="mergeGroupName" className="text-sm">
                        Group Name *
                      </Label>
                      <Input
                        id="mergeGroupName"
                        value={mergeGroupName}
                        onChange={(e) => setMergeGroupName(e.target.value)}
                        placeholder="e.g., Birthday Party, VIP Section"
                        className="bg-white"
                      />
                      <p className="text-xs text-blue-600">
                        This group will be saved for easier management
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm">Group Color</Label>
                      <div className="grid grid-cols-4 gap-2">
                        {[
                          { name: 'Blue', value: '#3B82F6' },
                          { name: 'Green', value: '#10B981' },
                          { name: 'Purple', value: '#8B5CF6' },
                          { name: 'Orange', value: '#F59E0B' },
                          { name: 'Red', value: '#EF4444' },
                          { name: 'Pink', value: '#EC4899' },
                          { name: 'Indigo', value: '#6366F1' },
                          { name: 'Teal', value: '#14B8A6' },
                        ].map((color) => (
                          <button
                            key={color.value}
                            type="button"
                            onClick={() => setMergeGroupColor(color.value)}
                            className={`h-10 rounded-md border-2 transition-all hover:scale-105 ${
                              mergeGroupColor === color.value
                                ? 'border-blue-600 ring-2 ring-blue-300'
                                : 'border-gray-300'
                            }`}
                            style={{ backgroundColor: color.value }}
                            title={color.name}
                          >
                            {mergeGroupColor === color.value && (
                              <Check className="h-5 w-5 text-white mx-auto" />
                            )}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-white rounded-md border border-blue-200">
                      <span className="text-sm text-gray-700">Total Seats:</span>
                      <span className="text-lg font-bold text-blue-600">
                        {getTotalCapacity()} seats
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* SMS Message */}
          <div className="space-y-2">
            <Label htmlFor="sms-message" className="text-sm font-medium">
              SMS Message
            </Label>
            <textarea
              id="sms-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter your SMS message here..."
            />
            <p className="text-xs text-gray-500">
              Message to: {reservation.phone}
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4">
            <Button
              variant="red"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              variant="blueBg"
              onClick={handleSend}
              disabled={isLoading || !message.trim() || (action === 'accept' && selectedTableIds.length === 0) || (selectedTableIds.length >= 2 && !mergeGroupName.trim())}
              className="flex items-center gap-2"
            >
              <Send className="h-4 w-4" />
              {isLoading ? 'Sending...' : 'Confirm'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}